/**
 * Transaction history page.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShoppingCart,
  Activity,
  Coins,
  RefreshCw,
  ArrowLeft,
  Filter,
  RotateCcw,
  ShieldOff,
} from 'lucide-react';
import { getTransactions } from '../services';
import type { Transaction, TransactionType } from '../types';

const TYPE_LABELS: Record<TransactionType, string> = {
  purchase: 'Purchase',
  usage: 'Usage',
  refund: 'Refund',
  'auto-renew': 'Auto-Renew',
  rotation: 'Token Rotated',
  revoke: 'Token Revoked',
};

export default function TransactionsPage() {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<TransactionType | 'all'>('all');

  useEffect(() => {
    async function load() {
      const res = await getTransactions();
      if (res.data) setTransactions(res.data);
      setLoading(false);
    }
    load();
  }, []);

  const filtered =
    filter === 'all'
      ? transactions
      : transactions.filter((t) => t.type === filter);

  if (loading) {
    return (
      <div className="page-container transactions-page-shell">
        <div className="loading-screen">
          <div className="spinner" />
          <p>Loading transactions…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container transactions-page-shell">
      <div className="container transactions-page-container">
        <div className="page-header">
          <div>
            <h1>Transaction History</h1>
            <p>{transactions.length} transactions</p>
          </div>
          <button className="btn btn-ghost" onClick={() => navigate('/dashboard')}>
            <ArrowLeft size={18} />
            Dashboard
          </button>
        </div>

        {/* Filter tabs */}
        <div className="filter-tabs">
          <Filter size={16} />
          {(['all', 'purchase', 'usage', 'refund'] as const).map((f) => (
            <button
              key={f}
              className={`filter-tab ${filter === f ? 'active' : ''}`}
              onClick={() => setFilter(f)}
            >
              {f === 'all' ? 'All' : TYPE_LABELS[f]}
            </button>
          ))}
        </div>

        {/* Transaction list */}
        <div className="card transactions-list-card">
          <div className="card-body">
            {filtered.length === 0 ? (
              <p className="empty-state">No transactions found.</p>
            ) : (
              <div className="txn-list full transactions-scroll">
                {filtered.map((txn) => (
                  <div key={txn.id} className="txn-item">
                    <div className={`txn-icon ${txn.type}`}>
                      {txn.type === 'purchase' && <ShoppingCart size={16} />}
                      {txn.type === 'usage' && <Activity size={16} />}
                      {txn.type === 'refund' && <Coins size={16} />}
                      {txn.type === 'auto-renew' && <RefreshCw size={16} />}
                      {txn.type === 'rotation' && <RotateCcw size={16} />}
                      {txn.type === 'revoke' && <ShieldOff size={16} />}
                    </div>
                    <div className="txn-details">
                      <span className="txn-desc">{txn.description}</span>
                      <span className="txn-meta">
                        {txn.appName && <span className="txn-app">{txn.appName}</span>}
                        {txn.model && <span className="txn-model">{txn.model}</span>}
                        <span>{new Date(txn.createdAt).toLocaleString()}</span>
                      </span>
                    </div>
                    <div className="txn-right">
                      <div className={`txn-amount ${txn.tokens > 0 ? 'positive' : txn.tokens === 0 ? 'neutral' : 'negative'}`}>
                        {txn.tokens === 0 ? '—' : `${txn.tokens > 0 ? '+' : ''}${txn.tokens.toLocaleString()} tokens`}
                      </div>
                      {txn.amountUsd !== undefined && (
                        <div className="txn-usd">${txn.amountUsd.toFixed(2)}</div>
                      )}
                    </div>
                    <div className={`txn-status ${txn.status}`}>
                      {txn.status}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
